import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, injectIntl } from "react-intl";
import DataGrid, { DataGridItem } from "components/data-grid";
import FormTextField from "components/form/form-textfield";
import styles from "./styles/form-image-size.css.js";
import withStyles from "components/with-styles";
import FormCheckbox from "components/form/form-checkbox";
import SizeWarning from "./size-warning";

const computeSize = ({ aspectRatio, type, newSize: value, maintainAspectRatio, oldSize }) => {
  switch (type) {
    case "x":
      return maintainAspectRatio ? { x: value, y: Math.round(value / aspectRatio) } : { x: value, y: oldSize.y };
    case "y":
      return maintainAspectRatio ? { x: Math.round(aspectRatio * value), y: value } : { x: oldSize.x, y: value };
    default:
      return {};
  }
};

const FormImageSize = ({
  classes,
  intl,
  id,
  name,
  label,
  onChange,
  value: assetSize = {},
  originalSize = {},
  ...props
}) => {
  const handleFocus = event => event.target.select();
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  const aspectRatio = useMemo(() => (!originalSize || originalSize.y === 0 ? 1 : originalSize.x / originalSize.y), [
    originalSize,
  ]);

  const resetAssetValues = useCallback(
    () => {
      handleChange("x")(`${name}.x`)(originalSize ? originalSize.x : 0);
      handleChange("y")(`${name}.y`)(originalSize ? originalSize.y : 0);
    },
    [handleChange, name, originalSize],
  );

  const getValidatedNewSize = useCallback(
    (type, value, maintainAspectRatio) => {
      const validValue = isFinite(value);

      if (validValue) {
        const newSize = computeSize({
          aspectRatio,
          type,
          newSize: value,
          maintainAspectRatio,
          oldSize: assetSize,
        });
        return newSize;
      }
      return null;
    },
    [aspectRatio, assetSize],
  );

  const handleChange = useCallback(
    type => fieldName => value => {
      const newSize = getValidatedNewSize(type, value, maintainAspectRatio);
      if (newSize !== null) {
        onChange(name)(newSize);
      }
    },
    [getValidatedNewSize, maintainAspectRatio, name, onChange],
  );

  useEffect(
    () => {
      if (assetSize) {
        const { x, y } = assetSize;
        const { x: maxX, y: maxY } = originalSize;
        setShowWarning(x > maxX || y > maxY);
      }
    },
    [assetSize, originalSize],
  );

  const handleChangeAspectRatio = fieldName => checked => {
    setMaintainAspectRatio(checked);
    if (checked) {
      const newSize = getValidatedNewSize("x", assetSize.x, checked);
      if (newSize !== null) {
        onChange(name)(newSize);
      }
    }
  };

  return (
    <DataGrid withPadding={false}>
      <DataGridItem>
        <FormTextField
          disabled={assetSize === null}
          type="number"
          id={`${id}.x`}
          name={`${name}.x`}
          value={assetSize ? assetSize.x : 0}
          onChange={handleChange("x")}
          label={label.x}
          onFocus={handleFocus}
          {...props}
        />
      </DataGridItem>
      <DataGridItem>
        <FormTextField
          disabled={assetSize === null}
          type="number"
          id={`${id}.y`}
          name={`${name}.y`}
          value={assetSize ? assetSize.y : 0}
          onChange={handleChange("y")}
          label={label.y}
          onFocus={handleFocus}
          {...props}
        />
      </DataGridItem>
      {showWarning && <SizeWarning size={originalSize} />}
      <div className={classes.options}>
        <FormCheckbox
          labelClassName={classes.checkboxLabel}
          id={`${name}.maintainAspectRatio`}
          name={`${name}.maintainAspectRatio`}
          value="updatable"
          checked={maintainAspectRatio}
          onChange={handleChangeAspectRatio}
          label={label.maintainAspectRatio}
        />
        <div>
          <FormattedMessage
            id="graphicset.layout.originalSize"
            values={{ xValue: originalSize ? originalSize.x : 0, yValue: originalSize ? originalSize.y : 0 }}
          />
          <div className={classes.resetLink} onClick={resetAssetValues}>
            {`(${intl.formatMessage({ id: "graphicset.layout.reset" })})`}
          </div>
        </div>
      </div>
    </DataGrid>
  );
};

FormImageSize.propTypes = {
  /**
   * Input id, will add .x or .y depeding on the current input data
   **/
  id: PropTypes.string,
  /**
   * Input name, will add .x or .y depeding on the current input data
   **/
  name: PropTypes.string,
  /**
   * Object with size of the image respect to originalSize, in coordinates [0,2]
   **/
  value: PropTypes.object,
  /**
   * Object with original size of the image, in px
   **/
  originalSize: PropTypes.object,
  /**
   * Labels for the inputs {x, y, maintainAspectRatio}
   **/
  label: PropTypes.object,
  /**
   * Formik callback for changing value
   **/
  onChange: PropTypes.func,
};

export default withStyles(styles)(injectIntl(FormImageSize));
